// Vue 3 i18n setup (vue-i18n v9, Composition mode with global injection so that
// both `{{ $t('…') }}` in templates and `this.$t('…')` in Options-API methods work).
//
// Four locales are supported. The active one is persisted in localStorage and
// mirrored onto <html lang="…"> for accessibility / SEO. The same locale code is
// sent to the backend (see src/store/api.js) so DB content comes back translated.
import { createI18n } from 'vue-i18n'
import uzLatn from './locales/uz-Latn'
import ru from './locales/ru'
import en from './locales/en'

// Order here is the order shown in the language switcher.
export const LOCALES = [
  { code: 'uz-Latn', name: 'Oʻzbek',  short: 'Oʻzb' },
  { code: 'ru',      name: 'Русский', short: 'Рус' },
  { code: 'en',      name: 'English', short: 'Eng' }
]

export const DEFAULT_LOCALE = 'uz-Latn'
const STORAGE_KEY = 'trm_locale'
const SUPPORTED = LOCALES.map(l => l.code)

// ── URL language prefixes ─────────────────────────────────────────────────────
export const URL_LANGS = ['uz', 'ru', 'en']
const LOCALE_TO_LANG = { 'uz-Latn': 'uz', ru: 'ru', en: 'en' }

/** URL prefix for a locale code. */
export function langForLocale(code = getLocale()) {
  return LOCALE_TO_LANG[code] || 'uz'
}

/**
 * Locale a URL prefix should render in.
 */
export function localeForLang(lang) {
  if (lang === 'ru' || lang === 'en') return lang
  return DEFAULT_LOCALE
}

const LANG_PREFIX_RE = new RegExp(`^/(?:${URL_LANGS.join('|')})(?=/|$)`)

/** Drop the language prefix: "/ru/markaz/tuzilma" → "/markaz/tuzilma". */
export function stripLang(path) {
  return String(path || '/').replace(LANG_PREFIX_RE, '') || '/'
}

/** Put `path` under a language prefix, replacing one it may already carry. */
export function withLang(path, lang = langForLocale()) {
  const bare = stripLang(path)
  return `/${lang}${bare === '/' ? '' : bare}`
}

/** The locale the visitor explicitly chose in the switcher, if any. */
function savedLocale() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved && SUPPORTED.includes(saved)) return saved
  } catch (e) { /* localStorage unavailable */ }
  return null
}

// First visit is always Uzbek. The browser's own language is deliberately NOT
// consulted: most visitors run a Russian-locale OS/browser, which used to open
// the site in Russian for everyone. A choice made in the switcher is remembered.
function detectLocale() {
  return savedLocale() || DEFAULT_LOCALE
}

const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: detectLocale(),
  fallbackLocale: DEFAULT_LOCALE,
  messages: {
    'uz-Latn': uzLatn,
    ru,
    en
  }
})

// Map our locale codes to a valid BCP-47 value for the <html lang> attribute.
const HTML_LANG = { 'uz-Latn': 'uz-Latn', ru: 'ru', en: 'en' }

export function getLocale() {
  return i18n.global.locale.value
}

/**
 * Switch the active locale.
 *
 * `persist` separates the two ways a language gets chosen. Picking one in the
 * switcher is a preference and is remembered; following a /ru link only sets
 * the language for that visit. Without the distinction, opening one foreign
 * link would silently overwrite the reader's own choice for every later visit.
 */
export function setLocale(code, { persist = true } = {}) {
  if (!SUPPORTED.includes(code)) return
  if (persist) {
    try { localStorage.setItem(STORAGE_KEY, code) } catch (e) { /* ignore */ }
  }
  if (code === getLocale()) return
  i18n.global.locale.value = code
  applyHtmlLang(code)
}

export function applyHtmlLang(code = getLocale()) {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('lang', HTML_LANG[code] || 'uz')
  }
}

// Reflect the initial locale on first load.
applyHtmlLang()

export default i18n
